import { PlayByPlayModelDto, GameModelDto, GamePbpModelDto, TeamModelDto } from "../api/models";
import { DefenseSystemType, EventDto, EventType, GameDto, GameModelSnapshotDto, OffenseSystemType, PhaseType } from "./games.service";
import { Team } from "./teams.service";

/**
 * The snapshot (which carries the elite custom buttons) travels to the cloud as a JSON
 * string. Games synced before it was actually uploaded hold the literal '{}', so an
 * empty object is treated as "nothing came back" and the caller keeps its local copy —
 * otherwise a re-sync would wipe the buttons off the device that configured them.
 */
const parseGameModelSnapshot = (raw: string | null | undefined): GameModelSnapshotDto | undefined => {
    if (!raw) return undefined;
    try {
        const parsed = JSON.parse(raw) as GameModelSnapshotDto;
        return parsed && Object.keys(parsed).length > 0 ? parsed : undefined;
    } catch {
        // Deliberately louder than the "never synced" case above: a snapshot that is present but
        // unparseable degrades elite tagging to the generic filters with nothing else to show for it,
        // and it is indistinguishable from an empty one by the time the caller sees `undefined`.
        console.error('Could not parse gameModelSnapshot, ignoring it', raw);
        return undefined;
    }
};

/**
 * True when the local record actually carries a snapshot. Games persisted before the snapshot was
 * uploaded hold `undefined` here (the old cloud->local mapper simply omitted the key), and an
 * emptied `{}` means the same thing to the read side — see `parseGameModelSnapshot`.
 */
const hasGameModelSnapshot = (snapshot: GameModelSnapshotDto | undefined): boolean =>
    !!snapshot && Object.keys(snapshot).length > 0;

/**
 * The game carries a reference to each team, not the full team record. Server ids are the only key
 * the cloud speaks, so the local id is resolved through the synced teams map, falling back to what
 * the local game already had — otherwise a team missing from that map would break the link and
 * `mapLocalGameToCloudGame` would then throw 'Teams not found' on the next push.
 */
const mapCloudTeamToLocalTeam = (uuid: string, name: string, teamsMapByServerId: Record<string, TeamModelDto>, localTeam?: Team): Team => ({
    uuid,
    name,
    localId: teamsMapByServerId[uuid]?.localId ?? localTeam?.localId ?? '',
    localLastUpdate: localTeam?.localLastUpdate ?? null,
    pendingDelete: localTeam?.pendingDelete ?? false,
});

export const mapCloudPbpToLocalPbp = (pbp: PlayByPlayModelDto, game: GameDto): EventDto => ({
    uuid: pbp.uuid,
    localId: pbp.localId,
    orderId: pbp.orderId,
    important: pbp.important,
    eventType: pbp.event === 'recording' ? undefined : (pbp.event as EventType),
    phase: pbp.phase as PhaseType,
    offenseSystem: pbp.offenseSystem as OffenseSystemType,
    defenseSystem: pbp.defenseSystem as DefenseSystemType,
    gameLocalId: game.localId,
    teamMarker: pbp.teamMarker === 'home' ? 'home' : 'visitor',
    eventDate: new Date(pbp.timestampColumn),
    videoUri: '',
    cloudVideoUploaded: pbp.videoUrl !== null,
    gameSecondsSinceStart: pbp.secondsSinceStartOfGame,
    halftime: pbp.halftime ?? '',
    minutesSinceHalftime: pbp.minutesSinceHalftime ?? 0,
    secondsSinceHalftime: pbp.secondsSinceHalftime ?? 0,
    lastPresignedCloudVideoUrl: pbp.videoUrl && (pbp.videoUrl ?? 'false').startsWith('https://') ? pbp.videoUrl! : undefined,
    lastPresignedCloudVideoTimestamp: pbp.videoUrl && (pbp.videoUrl ?? 'false').startsWith('https://') ? Date.now() : undefined,
    gameSystem: pbp.gameSystem ? JSON.parse(pbp.gameSystem) : undefined,
});

export const mapLocalPbpToCloudPbp = (pbp: EventDto, game: GameDto): PlayByPlayModelDto => ({
    uuid: pbp.uuid, // Sometimes undefined when creating a new pbp
    localId: pbp.localId,
    orderId: pbp.orderId,
    important: pbp.important,
    event: pbp.eventType ?? 'recording',
    teamMarker: pbp.teamMarker,
    teamName: pbp.teamMarker === 'home' ? game.homeTeam.name : game.visitorTeam.name,
    teamUuid: pbp.teamMarker === 'home' ? game.homeTeam.uuid! : game.visitorTeam.uuid!,
    eventDuration: null,
    executionPosition: null,
    possessionId: 0,
    possessions: 0,
    phase: pbp.phase ?? '',
    halftime: pbp.halftime ?? '',
    minutesSinceHalftime: pbp.minutesSinceHalftime ?? 0,
    secondsSinceHalftime: pbp.secondsSinceHalftime ?? 0,
    secondsSinceStartOfGame: pbp.gameSecondsSinceStart ?? 0,
    timestampColumn: new Date(pbp.eventDate).toISOString(),
    gameSystem: pbp.gameSystem ? JSON.stringify(pbp.gameSystem) : null,
    shotLocation: null,
    assistantPlayer: null,
    offensePlayer: null,
    defensePlayer: null,
    offenseSystem: pbp.offenseSystem ?? '',
    defenseSystem: pbp.defenseSystem ?? '',
    offense: '',
    defense: '',
    videoUrl: null
});

/**
 * Annotated with an explicit `GameDto` return (rather than an `as GameDto` cast) on purpose: the cast
 * is what let this mapper ship for a while without emitting `gameModelSnapshot` at all, which is the
 * root of the legacy records `mapLocalGameToCloudGame` now has to defend against.
 */
export const mapCloudGameToLocalGame = (cloudGame: GameModelDto, teamsMapByServerId: Record<string, TeamModelDto>, localGame?: GameDto): GameDto => ({
    // Incoming from cloud update (if local game exists is just an update from cloud or parse after send to cloud, if not exists is a new game coming from cloud)
    uuid: cloudGame.uuid,
    gameMode: cloudGame.gameType === 'elite' ? 'elite' : 'rookie',
    gameStatus: cloudGame.gameStatus === 'ended' ? 'ended' : 'live',
    trackerMode: localGame?.trackerMode ?? 'bench',
    localId: cloudGame.localId,
    // Optional on the wire, required locally: a game the cloud has no hash for keeps the one it was
    // already paired with rather than losing the ability to be joined.
    accessHash: cloudGame.accessHash ?? localGame?.accessHash ?? '',
    localConnectionInfo: cloudGame.localConnectionInfo,
    // Without this a bench that reached the game through the cloud has no custom elite
    // buttons, so the game-system folders silently fall back to the generic filters.
    gameModelSnapshot: parseGameModelSnapshot(cloudGame.gameModelSnapshot) ?? localGame?.gameModelSnapshot ?? {},
    gameDate: cloudGame.date,
    homeTeam: mapCloudTeamToLocalTeam(cloudGame.homeTeamUuid, cloudGame.home, teamsMapByServerId, localGame?.homeTeam),
    visitorTeam: mapCloudTeamToLocalTeam(cloudGame.visitorTeamUuid, cloudGame.visitor, teamsMapByServerId, localGame?.visitorTeam),
    createdAt: cloudGame.createdAt ?? localGame?.createdAt ?? new Date().toISOString(),
    localLastUpdate: null,
    pendingDelete: false,
});

/**
 * `existingCloudGameModelSnapshot` is the raw string the cloud currently holds for this game, and it is
 * only relevant on the update path. Games persisted before the snapshot round trip existed hold no
 * local snapshot at all, so serialising theirs would push the literal '{}' over a perfectly good cloud
 * record and strip the elite custom buttons off every other device. When there is nothing local worth
 * sending, the cloud value is echoed back untouched instead.
 */
export const mapLocalGameToCloudGame = async (game: GameDto, teamsMapByLocalId: Record<string, TeamModelDto>, gameEvents: EventDto[] = [], localConnectionInfo: string = 'no-info', existingCloudGameModelSnapshot?: string): Promise<GamePbpModelDto> => {
    const homeTeam = teamsMapByLocalId[game.homeTeam.localId];
    const visitorTeam = teamsMapByLocalId[game.visitorTeam.localId];
    if (!homeTeam || !visitorTeam) throw new Error('Teams not found');

    game.homeTeam.uuid = homeTeam.uuid;
    game.visitorTeam.uuid = visitorTeam.uuid;

    return {
        uuid: game.uuid, // Sometimes undefined when creating a new game
        localId: game.localId,
        localConnectionInfo,
        gameType: game.gameMode === 'elite' ? 'elite' : 'rookie',
        gameMode: 'LIVE_MODE',
        date: game.gameDate,
        home: game.homeTeam.name,
        visitor: game.visitorTeam.name,
        gameStatus: game.gameStatus,
        homeTeamUuid: game.homeTeam.uuid!,
        visitorTeamUuid: game.visitorTeam.uuid!,
        accessHash: game.accessHash,
        goalsHome: 0,
        goalsVisitor: 0,
        firstHalfEnded: true,
        secondHalfEnded: true,
        gameModelSnapshot: hasGameModelSnapshot(game.gameModelSnapshot) ? JSON.stringify(game.gameModelSnapshot) : (existingCloudGameModelSnapshot ?? '{}'),
        versionNumber: '0.0.0',

        pbp: gameEvents?.length > 0 ? gameEvents.map(p => mapLocalPbpToCloudPbp(p, game)) : undefined,
    };
};
